import type { IconName } from "@rally/ui-kit";
import { titleOf } from "../chat/panel/title.ts";
import { say } from "../localization/say.ts";

export type Found = {
  fragment: string;
  conversations: { id: string; title: string; icon: string | null; lastAt: string | null }[];
  messages: {
    id: string;
    conversation: string;
    title: string | null;
    by: string;
    handle: string;
    text: string;
    at: string;
  }[];
  files: {
    id: string;
    name: string;
    conversation: string;
    title: string | null;
    bytes: number;
    at: string;
  }[];
  people: { account: string; handle: string; displayName: string | null; avatar: string | null }[];
  agents: {
    account: string;
    handle: string;
    displayName: string | null;
    avatar: string | null;
    model: string;
  }[];
  repositories: { project: string; projectName: string; name: string; url: string }[];
  apps: { id: string; placement: string; name: string; description: string; conversation: string }[];
};

export const NOTHING_FOUND: Found = {
  fragment: "",
  conversations: [],
  messages: [],
  files: [],
  people: [],
  agents: [],
  repositories: [],
  apps: [],
};

export type Source = "conversations" | "messages" | "files" | "people" | "agents" | "repositories" | "apps";
export type Filter = "all" | Source;

export type Row = {
  kind: "conversation" | "message" | "file" | "person" | "agent" | "repository" | "app";
  key: string;
  title: string;
  subtitle?: string;
  icon: IconName;
  goes: string;
  at?: string | null;
};

export type Group = {
  id: Source | "suggested";
  label: string;
  rows: Row[];
};

const named = (value: string | null | undefined): string | null => (value?.trim() ? value.trim() : null);

export function groupsOf(found: Found, filter: Filter = "all"): Group[] {
  const groups: Group[] = [
    {
      id: "conversations",
      label: say("find.conversations"),
      rows: found.conversations.map((one) => ({
        kind: "conversation",
        key: `conversation:${one.id}`,
        title: titleOf({ title: one.title }, ""),
        icon: "messages-square",
        goes: `/c/${one.id}`,
        at: one.lastAt,
      })),
    },
    {
      id: "messages",
      label: say("find.messages"),
      rows: found.messages.map((one) => ({
        kind: "message",
        key: `message:${one.id}`,
        title: one.text,
        subtitle: one.title?.trim()
          ? say("find.saidIn")(one.handle, one.title)
          : say("find.saidBy")(one.handle),
        icon: "message",
        goes: `/c/${one.conversation}?message=${encodeURIComponent(one.id)}`,
        at: one.at,
      })),
    },
    {
      id: "files",
      label: say("find.files"),
      rows: found.files.map((one) => ({
        kind: "file",
        key: `file:${one.id}`,
        title: one.name,
        subtitle: named(one.title) ?? say("chat.unnamed"),
        icon: "file-text",
        goes: `/c/${one.conversation}?file=${encodeURIComponent(one.id)}`,
        at: one.at,
      })),
    },
    {
      id: "people",
      label: say("find.people"),
      rows: found.people.map((one) => ({
        kind: "person",
        key: `person:${one.account}`,
        title: named(one.displayName) ?? one.handle,
        subtitle: named(one.displayName) ? `@${one.handle}` : say("find.person"),
        icon: "user",
        goes: `/people/${one.account}`,
      })),
    },
    {
      id: "agents",
      label: say("find.agents"),
      rows: found.agents.map((one) => ({
        kind: "agent",
        key: `agent:${one.account}`,
        title: named(one.displayName) ?? one.handle,
        subtitle: `@${one.handle} · ${one.model}`,
        icon: "bot",
        goes: `/agents/${one.account}`,
      })),
    },
    {
      id: "apps",
      label: say("find.apps"),
      rows: found.apps.map((one) => ({
        kind: "app",
        key: `app:${one.placement}`,
        title: one.name,
        subtitle: named(one.description) ?? say("find.app"),
        icon: "grid",
        goes: `/c/${one.conversation}?app=${encodeURIComponent(one.placement)}`,
      })),
    },
    {
      id: "repositories",
      label: say("find.repositories"),
      rows: found.repositories.map((one) => ({
        kind: "repository",
        key: `repository:${one.project}:${one.name}`,
        title: one.name,
        subtitle: `${one.projectName} · ${one.url}`,
        icon: "code",
        goes: `/repos/${one.project}?name=${encodeURIComponent(one.name)}`,
      })),
    },
  ];
  return groups.filter((group) => group.rows.length > 0 && (filter === "all" || group.id === filter));
}

export function walkable(groups: Group[]): Row[] {
  return groups.flatMap((group) => group.rows);
}

export function foundNothing(groups: Group[]): boolean {
  return groups.length === 0;
}

export function stepTo(count: number, focused: number, by: 1 | -1): number {
  if (count === 0) return -1;
  if (focused < 0) return by === 1 ? 0 : count - 1;
  return (focused + by + count) % count;
}

export function onEscape(state: { typed: string; open: boolean }): { typed: string; open: boolean } {
  if (state.typed !== "") return { typed: "", open: state.open };
  return { typed: "", open: false };
}
